// @flow
import React, { useCallback, useState, type Node } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'urql';
import Popover from '@ta/overlays.popover';
import { WithAuthzInfo } from '@ta/common.authz';
import {
  InteractionLogger,
  GlobalNavTrackingContext,
  TrackingNamespaceContext,
} from '@ta/tracking.interactions';
import trackEventAJAX from '@ta/common.tracking';
import { SocialLoginGate } from '@ta/social.login-gate';
import ActionsBlock from './ActionsBlock';
import {
  getPid,
  ShowOverlayUploaderModal,
  showLinkUploaderFromVideoUploader,
} from './ContentCollectionUtility';
import ProfileUrlQuery from './ProfileUrlQuery.gql';

type OwnProps = {|
  children: ({|
    ref: (?HTMLElement) => void,
    onClick: (SyntheticMouseEvent<*>) => mixed,
  |}) => Node,
|};

type Props = {|
  ...OwnProps,
  /** current uid of the page. */
  uid: string | null,
  /** currentLocale of the page. */
  currentLocale: string,
  /** current pageName of the page. */
  pageName: string,
  /** current servlet name of the page. Sometimes it is different than pageName. I need to use this in the case like : on Hotels Lander Page,pageName is Home but
   * initialServletName is Hotels */
  initialServletName: string,
  /** current locationId of the page. */
  currentLocationId: number,
  loggedInUserId: ?string,
|};

/**
 * Provides a callback to start posting content. This can be used to provide an alternate UI
 * for {@link ./ContentCollection}.
 */
const ContentCollectionProvider = ({
  children,
  currentLocale,
  pageName,
  currentLocationId,
  loggedInUserId,
  uid,
}: Props) => {
  /** The modalname of the modal the need to be rendered. */
  const [modalName, setModalName] = useState('');

  /** The pid of the modal that need to be passed into the modal. */
  const [pid, setPid] = useState(-1);

  /** To see if we need to render the LinkUploader from the link of video uploader. */
  const [showLinkUploader, setShowLinkUploader] = useState(false);

  /** The method is passed into ActionBlock to set the modal data in order to render coressponding modal in this class. */
  const setUploaderTriggerData = useCallback((newModalName: string, newPid: number) => {
    setModalName(newModalName);
    setPid(newPid);
  }, []);

  const [{ data }] = useQuery({
    query: ProfileUrlQuery,
    variables: { userId: loggedInUserId || '' },
    pause: loggedInUserId == null,
  });

  const profileUrl = data?.memberProfile?.route?.url || null;

  return (
    <TrackingNamespaceContext.Provider value="globalNav">
      <GlobalNavTrackingContext.Provider value={{ uid }}>
        <SocialLoginGate pid={getPid(pageName)}>
          {loginGate => (
            <InteractionLogger>
              {logClick => {
                const trackedOnClick = (event: SyntheticMouseEvent<*>) => {
                  logClick(event, 'postContentCtaClick', {
                    action: 'NO_SELECTION',
                    entrypoint: 'GLOBAL_NAV',
                    locationId: currentLocationId,
                    isMemberSignedIn: loggedInUserId != null,
                  });
                  trackEventAJAX({
                    module: 'postContentCtaClick',
                    action: 'NO_SELECTION',
                    context: `GLOBAL_NAV|${pageName}`,
                    isMemberSignedIn: loggedInUserId != null,
                  });
                };
                return (
                  <>
                    {ShowOverlayUploaderModal(
                      modalName,
                      pid,
                      currentLocale,
                      profileUrl,
                      currentLocationId,
                      setShowLinkUploader,
                      pageName,
                    )}
                    {showLinkUploaderFromVideoUploader(
                      showLinkUploader,
                      currentLocale,
                      profileUrl,
                      currentLocationId,
                      pageName,
                    )}
                    <Popover
                      fullBleed
                      below
                      alignEdge="right"
                      contents={() => (
                        <WithAuthzInfo>
                          {(authzInfo, loading) => (
                            <ActionsBlock
                              permissions={authzInfo}
                              permissionsLoading={loading}
                              setUploaderTriggerDataMethod={setUploaderTriggerData}
                              loginGate={loginGate}
                            />
                          )}
                        </WithAuthzInfo>
                      )}
                      closeButton={false}
                      shiftArrow={-19}
                      shiftY={2}
                      arrowType="small"
                      fixed={pageName === 'Home'}
                      minWidth={200}
                    >
                      {(target, open) =>
                        children({
                          ref: target,
                          onClick: (event: SyntheticMouseEvent<*>) => {
                            open();
                            trackedOnClick(event);
                          },
                        })
                      }
                    </Popover>
                  </>
                );
              }}
            </InteractionLogger>
          )}
        </SocialLoginGate>
      </GlobalNavTrackingContext.Provider>
    </TrackingNamespaceContext.Provider>
  );
};

export default connect<Props, OwnProps, _, _, _, _>(state => ({
  uid: state.tracking.uid || '',
  currentLocale: state.i18n.locale,
  pageName: state.route.page,
  currentLocationId: state.route.detail || 0,
  initialServletName: state.meta.initialServletName,
  loggedInUserId: state.auth.loggedInUserId,
}))(ContentCollectionProvider);
